class TabsContainer extends HTMLElement {
  #loadedScripts = new Set();
  #navViewport = null;
  #navPrev = null;
  #navNext = null;
  #navResizeObserver = null;

  constructor() {
    super();

    this.tabs = null;
    this.panel = null;
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-ref="tab"]');
    this.panel = this.querySelector('[data-ref="tabs-panel"]');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.#resetTabs();
        this.#selectTab(tab);
      });
    });

    this.#selectTab(this.tabs[0]);

    if (this.dataset.showNavigationArrows === 'true') {
      this.#initNavigationArrows();
    }
  }

  disconnectedCallback() {
    this.#destroyNavigationArrows();
  }

  #initNavigationArrows() {
    this.#navViewport = this.querySelector('[data-ref="tabs-nav-viewport"]');
    this.#navPrev = this.querySelector('[data-ref="tabs-nav-arrow-prev"]');
    this.#navNext = this.querySelector('[data-ref="tabs-nav-arrow-next"]');

    if (!this.#navViewport || !this.#navPrev || !this.#navNext) return;

    this.#navViewport.addEventListener('scroll', this.#onNavScroll, { passive: true });
    this.#navPrev.addEventListener('click', this.#onNavPrevClick);
    this.#navNext.addEventListener('click', this.#onNavNextClick);

    this.#navResizeObserver = new ResizeObserver(() => this.#updateNavigationArrows());
    this.#navResizeObserver.observe(this.#navViewport);
    this.#navResizeObserver.observe(this);

    requestAnimationFrame(() => this.#updateNavigationArrows());
  }

  #destroyNavigationArrows() {
    if (this.#navViewport) {
      this.#navViewport.removeEventListener('scroll', this.#onNavScroll);
    }

    if (this.#navPrev) {
      this.#navPrev.removeEventListener('click', this.#onNavPrevClick);
    }

    if (this.#navNext) {
      this.#navNext.removeEventListener('click', this.#onNavNextClick);
    }

    this.#navResizeObserver?.disconnect();
    this.#navResizeObserver = null;
    this.#navViewport = null;
    this.#navPrev = null;
    this.#navNext = null;
  }

  #onNavScroll = () => {
    this.#syncNavigationArrowVisibility();
  };

  #onNavPrevClick = () => {
    const delta = Math.max(this.#navViewport.clientWidth * 0.6, 120);
    this.#navViewport.scrollBy({ left: -delta, behavior: 'smooth' });
  };

  #onNavNextClick = () => {
    const delta = Math.max(this.#navViewport.clientWidth * 0.6, 120);
    this.#navViewport.scrollBy({ left: delta, behavior: 'smooth' });
  };

  #updateNavigationArrows() {
    if (!this.#navViewport || !this.#navPrev || !this.#navNext) return;

    const { scrollWidth, clientWidth } = this.#navViewport;
    const canScroll = scrollWidth > clientWidth + 1;

    this.toggleAttribute('data-nav-arrows-active', canScroll);

    if (!canScroll) {
      this.#navPrev.hidden = true;
      this.#navNext.hidden = true;
      return;
    }

    this.#syncNavigationArrowVisibility();
  }

  #syncNavigationArrowVisibility() {
    if (!this.#navViewport || !this.#navPrev || !this.#navNext) return;

    const v = this.#navViewport;
    const epsilon = 2;

    this.#navPrev.hidden = v.scrollLeft <= epsilon;
    this.#navNext.hidden = v.scrollLeft + v.clientWidth >= v.scrollWidth - epsilon;
  }

  #selectTab(tab) {
    const tab_content = tab.querySelector('template').content.querySelector('[data-ref="tab-content"]');

    this.panel.innerHTML = tab_content.innerHTML;
    this.#activateScripts(this.panel);
    tab.classList.add('active');
  }

  #activateScripts(container) {
    for (const script of [...container.querySelectorAll('script')]) {
      const src = script.getAttribute('src');

      if (src) {
        if (this.#loadedScripts.has(src)) {
          script.remove();
          continue;
        }
        this.#loadedScripts.add(src);
      }

      const activeScript = document.createElement('script');

      for (const attr of script.attributes) {
        activeScript.setAttribute(attr.name, attr.value);
      }

      activeScript.textContent = script.textContent;
      script.replaceWith(activeScript);
    }
  }

  #resetTabs() {
    this.tabs.forEach((tab) => {
      tab.classList.remove('active');
    });
    this.panel.innerHTML = '';
  }
}

if (!customElements.get('tabs-container')) {
  customElements.define('tabs-container', TabsContainer);
}
